import { PDAExportResponse } from '@containers/common/da-tool/evaluation/BudgetStatus/GescoIntegrationInfo';
import {
  ExpenseEvaluationBudgetStatusRequest,
  ExpenseEvaluationBudgetStatusResponse
} from '../../../types';
import { evaluationApi } from '../api';

export const budgetApi = evaluationApi.injectEndpoints({
  endpoints: (build) => ({
    fetchBudgetStatus: build.query<
      ExpenseEvaluationBudgetStatusResponse,
      { evaluationId: number | string }
    >({
      query: ({ evaluationId }) => ({
        url: `/${evaluationId}/budget-check`,
        method: 'GET'
      }),
      providesTags: ['BudgetStatus']
    }),

    performBudgetCheck: build.mutation<
      ExpenseEvaluationBudgetStatusResponse,
      ExpenseEvaluationBudgetStatusRequest
    >({
      query: ({ evaluationId, ...data }) => ({
        url: `/${evaluationId}/budget-check`,
        data: {
          ...data,
          evaluationId: evaluationId
        },
        method: 'POST'
      }),
      async onQueryStarted({ evaluationId }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;

          dispatch(
            budgetApi.util.updateQueryData(
              'fetchBudgetStatus',
              { evaluationId },
              () => data
            )
          );
        } catch {
          //
        }
      },
      invalidatesTags: ['BudgetCommit']
    }),

    fetchBudgetCommitStatus: build.query<
      Map<string, PDAExportResponse>,
      { evaluationId: number }
    >({
      query: ({ evaluationId }) => ({
        url: `/${evaluationId}/budget-commit`,
        method: 'GET'
      }),
      providesTags: (result, _error, arg) =>
        result
          ? [{ type: 'BudgetCommit' as const, id: arg.evaluationId }]
          : ['BudgetCommit']
    }),

    performBudgetCommit: build.mutation<
      Map<string, PDAExportResponse>,
      { evaluationId: number }
    >({
      query: ({ evaluationId }) => ({
        url: `/${evaluationId}/budget-commit`,
        data: {
          evaluationId: evaluationId
        },
        method: 'POST'
      }),
      invalidatesTags: (result, _error, arg) =>
        result
          ? [{ type: 'BudgetCommit' as const, id: arg.evaluationId }]
          : ['BudgetCommit']
    }),

    sendBudgetCommitReminder: build.mutation<
      void,
      { evaluationId: number; departmentName: string }
    >({
      query: ({ evaluationId, departmentName }) => ({
        url: `/${evaluationId}/budget-commit/send-reminder/department/${departmentName}`,
        data: {
          evaluationId: evaluationId,
          departmentName: departmentName
        },
        method: 'POST'
      })
    })
  }),
  overrideExisting: false
});

export const {
  useFetchBudgetStatusQuery,
  usePerformBudgetCheckMutation,
  useFetchBudgetCommitStatusQuery,
  usePerformBudgetCommitMutation,
  useSendBudgetCommitReminderMutation
} = budgetApi;

export type fetchBudgetStatusResponse =
  typeof budgetApi.endpoints.fetchBudgetStatus.Types.ResultType;

export const invalidateBudgetStatus = budgetApi.util.invalidateTags([
  'BudgetStatus',
  'BudgetCommit'
]);
